import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import { css, styled } from '@mui/material';
import { checkEllipsis, observeResizeEvent, Tooltip, Typography } from '@kc-console/common-ui';

import EllipsisWrapper from './EllipsisWrapper';

import type { TooltipProps, TypographyProps } from '@kc-console/common-ui';
import type { EllipsisTextProps } from './types';

const EllipsisText = (props: EllipsisTextProps) => {
  const {
    className,
    children,
    color,
    message,
    direction = 'horizon',
    wordBreak = 'break-word',
    lineClamp = 2,
    placement = 'top',
    slotProps,
    variant,
    useCustomWrapper = false,
  } = props;

  const {
    wrapper: wrapperProps,
    tooltip: tooltipProps = { interactive: true },
    typography: typographyProps,
  } = slotProps ?? { wrapper: {}, tooltip: {}, typography: {} };

  const containerRef = useRef<HTMLElement>(null);
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(null);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const setContainerRef = useCallback((node: HTMLElement | null) => {
    containerRef.current = node;
    setContainerElement(prevNode => (prevNode === node ? prevNode : node));
  }, []);

  const handleShowTooltip = useCallback(() => {
    if (containerRef.current) {
      if (direction === 'vertical') {
        setShowTooltip(containerRef.current.offsetHeight < containerRef.current.scrollHeight);
      } else {
        setShowTooltip(checkEllipsis(containerRef.current));
      }
    }
  }, [direction]);

  useEffect(
    () => observeResizeEvent(containerRef, handleShowTooltip),
    [containerElement, handleShowTooltip],
  );

  useEffect(() => {
    handleShowTooltip();
  }, [children, containerElement, handleShowTooltip]);

  const refinedTooltipProps: Omit<TooltipProps, 'children'> = {
    ...tooltipProps,
    message: showTooltip ? message || children : '',
    placement,
    slotProps: {
      popper: {
        disablePortal: false,
        sx: {
          maxWidth: 500,
        },
        ...tooltipProps?.slotProps?.popper,
      },
    },
  };

  return (
    <EllipsisWrapper
      enable={!useCustomWrapper}
      {...wrapperProps}
      className={clsx(className, wrapperProps?.className)}
    >
      <Tooltip {...refinedTooltipProps}>
        <InlineWrapper>
          <EllipsisTextTypography
            ref={setContainerRef}
            color={color}
            variant={variant}
            ownerState={{ direction, lineClamp, wordBreak }}
            {...typographyProps}
          >
            {children}
          </EllipsisTextTypography>
        </InlineWrapper>
      </Tooltip>
    </EllipsisWrapper>
  );
};

export default EllipsisText;

const InlineWrapper = styled('span')(() => ({
  display: 'inline',
}));

const EllipsisTextTypography = styled(Typography, {
  name: 'EllipsisText',
  target: 'root',
  shouldForwardProp: prop => !['ownerState'].includes(prop as string),
})<
  TypographyProps & {
    ownerState: Pick<EllipsisTextProps, 'direction' | 'lineClamp' | 'wordBreak'>;
  }
>(
  ({ ownerState }) => css`
    overflow: hidden;
    ${ownerState.direction === 'vertical'
      ? css`
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: ${!ownerState.lineClamp ? 2 : ownerState.lineClamp};
          word-break: ${ownerState.wordBreak};
        `
      : css`
          display: block;
          white-space: nowrap;
          text-overflow: ellipsis;
        `}
  `,
);
